
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { isVendedorScopedRole } from "@/types/auth";

export interface Projeto {
  id: number; // Integer sequencial no banco
  cliente_id: number; // integer no banco
  orcamento_id?: number; // Integer sequencial no banco
  contrato_id?: number; // integer no banco
  vendedor_id?: string | null;
  status: string;
  departamento?: 'vendas' | 'financeiro' | 'tecnico' | 'administrativo';
  observacoes?: string;
  valor_projeto?: number;
  total_paineis?: number;
  created_at: string;
  updated_at: string;
  
  // Dados da empresa responsável
  empresa_responsavel_id?: number | null;
  empresa_responsavel?: string;
  cnpj_empresa?: string;
  telefone_empresa?: string;
  email_empresa?: string;
  is_projeto_elevo?: boolean;
  id_projeto_elevo?: string;
  empresa_projeto_externo_id?: string | null;
  
  // Dados do vendedor
  vendedor_nome?: string;
  vendedor_celular?: string;
  vendedor_cpf_cnpj?: string;
  
  // Dados do instalador
  instalador_nome?: string;
  instalador_cpf_cnpj?: string;
  instalador_contato?: string;
  
  // Dados de aprovação
  aprovado_por?: string;
  data_aprovacao?: string;
  
  // Dados de anexos
  vistoria_file_url?: string;
  vistoria_file_name?: string;
  contrato_file_url?: string;
  contrato_file_name?: string;
  nota_fiscal_file_url?: string;
  nota_fiscal_file_name?: string;
  outros_documentos_url?: string;
  outros_documentos_name?: string;
  
  // Consultor responsável
  consultor_id?: string;
  consultor_nome?: string;
  consultor_celular?: string;
  consultor_cpf_cnpj?: string;
  consultor_email?: string;
  
  cliente?: {
    id: number;
    nome: string;
    email?: string;
    telefone?: string;
    cpf_cnpj?: string;
    cidade?: string;
    estado?: string;
  };
  orcamento?: {
    id: number; // Integer sequencial no banco
    valor_total?: number;
    status?: string;
    plano_id?: string;
    quantidade_placas?: number;
    valor_seguro?: number;
    vigencia_anos?: number;
    vendedor_id?: string | null;
    observacoes?: string;
    created_at?: string;
    plano?: {
      id: string | number;
      nome: string;
    } | null;
  };
  contrato?: {
    id: number; // integer no banco
    valor: number;
    status: string;
    data_assinatura?: string;
    data_inicio?: string;
    data_fim?: string;
    arquivo_pdf_url?: string;
    numero_contrato?: string;
    created_at?: string;
  };
}

export interface ProjetoHistorico {
  id: number; // Integer sequencial no banco
  projeto_id: number; // Integer sequencial no banco
  status_anterior: string;
  status_novo: string;
  observacoes?: string;
  usuario_id?: string;
  created_at: string;
}

export type ProjetosSort = {
  field:
    | "id"
    | "created_at"
    | "departamento"
    | "valor_projeto"
    | "total_paineis"
    | "orcamento_id"
    | "cliente_nome"
    | "valor_seguro";
  direction: "asc" | "desc";
};

export type ProjetosFiltroParams = {
  periodo?: { dataInicio?: string; dataFim?: string } | null; // yyyy-mm-dd
  criadoPorId?: string | null; // projetos.vendedor_id
  estado?: string | null;      // cliente.estado (UF sigla)
  cidade?: string | null;      // cliente.cidade
  paineis?: { min?: number; max?: number } | null; // projetos.total_paineis
  valorProjeto?: { min?: number; max?: number } | null; // projetos.valor_projeto
  valorSeguro?: { min?: number; max?: number } | null; // orcamento.valor_seguro
};

// Hook para contar total de projetos (sem buscar dados)
export function useProjetosCount() {
  const { user, profile, loading } = useAuth();
  const resolvedRole = String((profile as any)?.user_role ?? profile?.role ?? '').toLowerCase();
  const isVendedor = isVendedorScopedRole(resolvedRole as any) && !!user?.id;

  return useQuery({
    queryKey: ['projetos', 'count', isVendedor ? user?.id : null],
    queryFn: async () => {
      let query = supabase.from('projetos').select('*', { count: 'exact', head: true });
      if (isVendedor) query = query.eq('vendedor_id', user!.id);
      const { count, error } = await query;

      if (error) {
        console.error('❌ Erro ao contar projetos:', error);
        throw error;
      }

      return count || 0;
    },
    enabled: !loading,
    staleTime: 60 * 1000, // 1 minuto - contagem muda menos frequentemente
    refetchOnWindowFocus: false,
  });
}

export function useTotalPaineisDashboard() {
  return useQuery({
    queryKey: ["dashboard", "total-paineis"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_total_paineis_dashboard");
      if (error) {
        console.error("❌ Erro ao buscar total de painéis (dashboard):", error);
        throw error;
      }
      return (data ?? 0) as number;
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Hook para buscar projetos paginados
export function useProjetosPaginated(
  page: number = 1,
  limit: number = 15,
  searchTerm?: string,
  filtroStatus?: string,
  sort?: ProjetosSort | null,
  filtros?: ProjetosFiltroParams | null
) {
  const { user, profile, loading } = useAuth();
  const resolvedRole = String((profile as any)?.user_role ?? profile?.role ?? '').toLowerCase();
  const isVendedor = isVendedorScopedRole(resolvedRole as any) && !!user?.id;

  return useQuery({
    queryKey: ['projetos', 'paginated', page, limit, searchTerm, filtroStatus, sort ?? null, filtros ?? null, isVendedor ? user?.id : null],
    queryFn: async () => {
      console.log(`🔍 Buscando projetos - Página ${page}, Limite ${limit}, Busca: ${searchTerm || 'nenhuma'}, Filtro: ${filtroStatus || 'todos'}...`);
      
      try {
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        // Detectar inner join no cliente quando há filtro por estado/cidade
        const clienteFilterPresent = !!(filtros?.estado || filtros?.cidade);
        const clienteEmbed = `cliente:clientes${clienteFilterPresent ? "!inner" : ""}(id, nome, email, telefone, cpf_cnpj, cidade, estado)`;

        // Construir query base
        let query = supabase
          .from('projetos')
          .select(`
            *,
            ${clienteEmbed},
            orcamento:orcamentos!orcamento_id(id, valor_total, status, observacoes, created_at, plano_id, quantidade_placas, valor_seguro, vigencia_anos, vendedor_id),
            contrato:contratos!projeto_id(id, valor, status, data_inicio, data_fim, arquivo_pdf_url)
          `, { count: 'exact' });

        if (isVendedor) query = query.eq('vendedor_id', user!.id);

        // Aplicar filtro de status se fornecido (normalizado)
        const dep = (filtroStatus || '').toLowerCase();
        if (dep && dep !== 'todos') {
          query = query.eq('departamento', dep);
        }

        // Filtros adicionais (relatório)
        if (filtros?.periodo?.dataInicio) {
          query = query.gte('created_at', `${filtros.periodo.dataInicio}T00:00:00`);
        }
        if (filtros?.periodo?.dataFim) {
          query = query.lte('created_at', `${filtros.periodo.dataFim}T23:59:59`);
        }
        if (filtros?.criadoPorId) {
          query = query.eq('vendedor_id', filtros.criadoPorId);
        }
        if (filtros?.estado) {
          query = query.eq('cliente.estado', filtros.estado.toUpperCase());
        }
        if (filtros?.cidade) {
          query = query.eq('cliente.cidade', filtros.cidade);
        }
        if (typeof filtros?.paineis?.min === 'number' && !Number.isNaN(filtros.paineis.min)) {
          query = query.gte('total_paineis', filtros.paineis.min);
        }
        if (typeof filtros?.paineis?.max === 'number' && !Number.isNaN(filtros.paineis.max)) {
          query = query.lte('total_paineis', filtros.paineis.max);
        }
        if (typeof filtros?.valorProjeto?.min === 'number' && !Number.isNaN(filtros.valorProjeto.min)) {
          query = query.gte('valor_projeto', filtros.valorProjeto.min);
        }
        if (typeof filtros?.valorProjeto?.max === 'number' && !Number.isNaN(filtros.valorProjeto.max)) {
          query = query.lte('valor_projeto', filtros.valorProjeto.max);
        }
        if (typeof filtros?.valorSeguro?.min === 'number' && !Number.isNaN(filtros.valorSeguro.min)) {
          query = query.gte('orcamento.valor_seguro', filtros.valorSeguro.min);
        }
        if (typeof filtros?.valorSeguro?.max === 'number' && !Number.isNaN(filtros.valorSeguro.max)) {
          query = query.lte('orcamento.valor_seguro', filtros.valorSeguro.max);
        }

        // Aplicar busca se fornecido
        if (searchTerm && searchTerm.trim()) {
          const searchLower = searchTerm.trim();
          const searchNumbersOnly = searchLower.replace(/\D/g, '');
          const clientesBase = () => {
            let q = supabase.from('clientes').select('id');
            if (isVendedor) q = q.eq('vendedor_id', user!.id);
            return q;
          };
          
          // Se for número puro (apenas dígitos)
          if (searchNumbersOnly.length > 0 && /^\d+$/.test(searchLower)) {
            // Se tiver até 6 dígitos, pode ser um ID - buscar por ID do projeto
            // Se tiver mais de 6 dígitos, provavelmente é CPF/CNPJ - buscar apenas em CPF/CNPJ do cliente
            if (searchNumbersOnly.length <= 6) {
              // Número pequeno: pode ser ID do projeto
              const idSearch = parseInt(searchLower);
              query = query.eq('id', idSearch);
            } else {
              // Número grande (CPF/CNPJ): buscar apenas em CPF/CNPJ do cliente
              const { data: clientesEncontrados } = await clientesBase().ilike('cpf_cnpj', `%${searchNumbersOnly}%`);
              
              if (clientesEncontrados && clientesEncontrados.length > 0) {
                const clienteIds = clientesEncontrados.map((c: { id: number }) => c.id);
                query = query.in('cliente_id', clienteIds);
              } else {
                query = query.eq('id', -1);
              }
            }
          } else {
            const { data: clientesEncontrados } = await clientesBase().or(`nome.ilike.%${searchLower}%,cpf_cnpj.ilike.%${searchLower}%`);
            
            if (clientesEncontrados && clientesEncontrados.length > 0) {
              const clienteIds = clientesEncontrados.map((c: { id: number }) => c.id);
              query = query.in('cliente_id', clienteIds);
            } else {
              query = query.eq('id', -1);
            }
          }
        }

        // Ordenar e paginar
        const sortField = sort?.field || "created_at";
        const sortAsc = (sort?.direction || "desc") === "asc";
        if (sortField === "cliente_nome") {
          query = query.order("nome", { ascending: sortAsc, foreignTable: "cliente" }).range(from, to);
        } else if (sortField === "valor_seguro") {
          query = query.order("valor_seguro", { ascending: sortAsc, foreignTable: "orcamento" }).range(from, to);
        } else {
          query = query.order(sortField, { ascending: sortAsc }).range(from, to);
        }

        const { data: projetosData, error: projetosError, count } = await query;

        if (projetosError) {
          console.error('❌ Erro ao buscar projetos:', projetosError);
          throw projetosError;
        }

        console.log(`✅ Projetos encontrados na página ${page}:`, projetosData?.length || 0, `Total: ${count || 0}`);
        return { data: ((projetosData || []) as unknown) as Projeto[], count: count || 0 };
      } catch (error) {
        console.error('💥 Erro geral na busca de projetos:', error);
        throw error;
      }
    },
    staleTime: 30 * 1000, // 30 segundos
    refetchOnWindowFocus: false, // Desabilitado para reduzir egresso
    gcTime: 5 * 60 * 1000, // 5 minutos de cache
    enabled: !loading,
  });
}

/** Projetos de um cliente (busca no servidor — não depende do limite do useProjetos) */
export function useProjetosPorCliente(clienteId?: string | number | null) {
  const { user, profile, loading } = useAuth();
  const resolvedRole = String((profile as any)?.user_role ?? profile?.role ?? "").toLowerCase();
  const isVendedor = isVendedorScopedRole(resolvedRole as any) && !!user?.id;
  const clienteIdNum = clienteId != null && clienteId !== "" ? Number(clienteId) : null;

  return useQuery({
    queryKey: ["projetos", "por-cliente", clienteIdNum, isVendedor ? user?.id : null],
    queryFn: async () => {
      let query = supabase
        .from("projetos")
        .select(
          `
          *,
          cliente:clientes(id, nome, email, telefone, cpf_cnpj, cidade, estado),
          orcamento:orcamentos!orcamento_id(id, valor_total, status, plano_id, quantidade_placas, valor_seguro),
          contrato:contratos!projeto_id(id, valor, status, data_inicio, data_fim)
        `
        )
        .eq("cliente_id", clienteIdNum!)
        .order("id", { ascending: false });

      if (isVendedor) query = query.eq("vendedor_id", user!.id);

      const { data, error } = await query;
      if (error) {
        console.error("❌ Erro ao buscar projetos do cliente:", error);
        throw error;
      }
      return (data || []) as Projeto[];
    },
    enabled: !loading && clienteIdNum != null && !Number.isNaN(clienteIdNum),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Hook legado - retorno simplificado com array de projetos
export function useProjetos(limit: number = 100, options?: { enabled?: boolean }) {
  const { user, profile, loading } = useAuth();
  const resolvedRole = String((profile as any)?.user_role ?? profile?.role ?? '').toLowerCase();
  const isVendedor = isVendedorScopedRole(resolvedRole as any) && !!user?.id;

  return useQuery({
    queryKey: ['projetos', 'default', limit, isVendedor ? user?.id : null],
    queryFn: async () => {
      let query = supabase
        .from('projetos')
        .select(`
          *,
          cliente:clientes(id, nome, email, telefone, cpf_cnpj),
          orcamento:orcamentos!orcamento_id(id, valor_total, status, observacoes, created_at, plano_id, quantidade_placas, valor_seguro, vigencia_anos),
          contrato:contratos!projeto_id(id, valor, status, data_inicio, data_fim, arquivo_pdf_url)
        `)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (isVendedor) query = query.eq('vendedor_id', user!.id);
      const { data, error } = await query;

      if (error) {
        console.error('❌ Erro ao buscar projetos (hook simples):', error);
        throw error;
      }

      return (data || []) as Projeto[];
    },
    enabled: (options?.enabled ?? true) && !loading,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useProjeto(id: number) {
  return useQuery({
    queryKey: ['projeto', id],
    queryFn: async () => {
      // Buscar projeto básico
      const { data: projeto, error: projetoError } = await supabase
        .from('projetos')
        .select('*')
        .eq('id', id)
        .single();

      if (projetoError) {
        console.error('Erro ao buscar projeto:', projetoError);
        throw projetoError;
      }

      const orcamentoSelect =
        'id, valor_total, valor_seguro, status, observacoes, created_at, plano_id, quantidade_placas, vigencia_anos, plano:planos_seguro(id, nome)';
      const contratoSelect =
        'id, valor, status, data_assinatura, data_inicio, data_fim, arquivo_pdf_url, numero_contrato, created_at';

      // Buscar dados relacionados (com fallback por projeto_id quando o vínculo direto falta)
      const [clienteResult, orcamentoById, orcamentoByProjeto, contratoById, contratoByProjeto] =
        await Promise.all([
          supabase.from('clientes').select('*').eq('id', projeto.cliente_id).single(),
          projeto.orcamento_id
            ? supabase.from('orcamentos').select(orcamentoSelect).eq('id', projeto.orcamento_id).maybeSingle()
            : Promise.resolve({ data: null, error: null }),
          supabase
            .from('orcamentos')
            .select(orcamentoSelect)
            .eq('projeto_id', id)
            .eq('status', 'aprovado')
            .eq('ativo', true)
            .order('data_aprovacao', { ascending: false })
            .limit(1)
            .maybeSingle(),
          projeto.contrato_id
            ? supabase.from('contratos').select(contratoSelect).eq('id', projeto.contrato_id).maybeSingle()
            : Promise.resolve({ data: null, error: null }),
          supabase
            .from('contratos')
            .select(contratoSelect)
            .eq('projeto_id', id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);

      // Preferir orçamento oficial do projeto; se o vínculo estiver desatualizado, usa o aprovado ativo
      const orcamentoVinculado = orcamentoById.data as { status?: string; ativo?: boolean } | null;
      const orcamentoOficial =
        orcamentoVinculado?.status === 'aprovado' && orcamentoVinculado.ativo !== false
          ? orcamentoById.data
          : orcamentoByProjeto.data;

      return {
        ...projeto,
        cliente: clienteResult.data,
        orcamento: orcamentoOficial,
        contrato: contratoById.data || contratoByProjeto.data,
      } as Projeto;
    },
    enabled: !!id,
    staleTime: 30000, // 30 segundos
  });
}

export function useSinistrosDoProjeto(projetoId: number) {
  return useQuery({
    queryKey: ['projeto-sinistros', projetoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sinistros')
        .select('id, tipo, status, descricao, data_ocorrencia, valor_estimado, valor_aprovado, created_at')
        .eq('projeto_id', projetoId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!projetoId,
    staleTime: 30_000,
  });
}

export function useServicosDoProjeto(projetoId: number) {
  return useQuery({
    queryKey: ['projeto-servicos', projetoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('servicos')
        .select('id, tipo_servico, status, descricao, data_solicitacao, data_agendada, data_execucao, valor, created_at')
        .eq('projeto_id', String(projetoId))
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!projetoId,
    staleTime: 30_000,
  });
}

export function usePedidosLimpezaDoProjeto(projetoId: number) {
  return useQuery({
    queryKey: ['projeto-limpeza', projetoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pedidos')
        .select('id, numero_pedido, limpeza_status, limpeza_data_agendada, limpeza_observacoes, status, created_at')
        .eq('projeto_id', projetoId)
        .not('limpeza_status', 'is', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!projetoId,
    staleTime: 30_000,
  });
}

export function useProjetoHistorico(projetoId: number) {
  return useQuery({
    queryKey: ['projeto-historico', projetoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projeto_historico')
        .select('*')
        .eq('projeto_id', projetoId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar histórico do projeto:', error);
        throw error;
      }

      return data as ProjetoHistorico[];
    },
    enabled: !!projetoId,
    staleTime: 30000, // 30 segundos
  });
}

export interface ProjetoEquipamento {
  id: number;
  quantidade: number;
  preco_unitario?: number;
  observacoes?: string;
  equipamento: {
    id: number;
    marca: string;
    modelo: string;
    tipo: string;
    potencia?: string;
    preco_unitario: number;
  };
}

export function useProjetoEquipamentos(projetoId: number) {
  return useQuery({
    queryKey: ['projeto-equipamentos', projetoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projeto_equipamentos')
        .select(`
          id,
          quantidade,
          preco_unitario,
          observacoes,
          equipamento:equipamentos(
            id,
            marca,
            modelo,
            tipo,
            potencia,
            preco_unitario
          )
        `)
        .eq('projeto_id', projetoId);

      if (error) {
        console.error('Erro ao buscar equipamentos do projeto:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!projetoId
  });
}

export function useAdicionarEquipamentoProjeto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projetoId,
      equipamentoId,
      quantidade,
      preco_unitario,
      observacoes,
    }: {
      projetoId: number;
      equipamentoId: number;
      quantidade: number;
      preco_unitario?: number;
      observacoes?: string;
    }) => {
      const payload = {
        projeto_id: projetoId,
        equipamento_id: equipamentoId,
        quantidade,
        preco_unitario: preco_unitario ?? null,
        observacoes: observacoes ?? null,
      };

      const { error } = await supabase
        .from("projeto_equipamentos")
        .insert(payload);

      if (error) {
        console.error("Erro ao adicionar equipamento ao projeto:", error);
        throw error;
      }

      return { success: true };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projeto-equipamentos", variables.projetoId] });
      queryClient.invalidateQueries({ queryKey: ["projetos"] });
    },
  });
}

export function useAtualizarEquipamentoProjeto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      dados,
    }: {
      id: number;
      dados: {
        quantidade?: number;
        preco_unitario?: number;
        observacoes?: string;
      };
    }) => {
      const payload = {
        quantidade: dados.quantidade ?? undefined,
        preco_unitario: dados.preco_unitario ?? null,
        observacoes: dados.observacoes ?? null,
      };

      const { error } = await supabase
        .from("projeto_equipamentos")
        .update(payload)
        .eq("id", id);

      if (error) {
        console.error("Erro ao atualizar equipamento do projeto:", error);
        throw error;
      }

      return { success: true };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projeto-equipamentos"] });
      queryClient.invalidateQueries({ queryKey: ["projetos"] });
    },
  });
}

export function useRemoverEquipamentoProjeto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const { error } = await supabase
        .from("projeto_equipamentos")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("Erro ao remover equipamento do projeto:", error);
        throw error;
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projeto-equipamentos"] });
      queryClient.invalidateQueries({ queryKey: ["projetos"] });
    },
  });
}

export function useAtualizarStatusProjeto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dados: {
      projetoId: number;
      novoStatus: string;
      observacoes?: string;
      statusAnterior: string;
    }) => {
      console.log('Atualizando status do projeto:', dados);

      // 1. Atualizar status do projeto
      const { error: updateError } = await supabase
        .from('projetos')
        .update({ status: dados.novoStatus })
        .eq('id', dados.projetoId);

      if (updateError) {
        console.error('Erro ao atualizar status do projeto:', updateError);
        throw updateError;
      }

      // 2. Registrar no histórico
      const { error: historicoError } = await supabase
        .from('projeto_historico')
        .insert({
          projeto_id: dados.projetoId,
          status_anterior: dados.statusAnterior,
          status_novo: dados.novoStatus,
          observacoes: dados.observacoes || null
        });

      if (historicoError) {
        console.error('Erro ao registrar histórico:', historicoError);
        throw historicoError;
      }

      console.log('Status do projeto atualizado com sucesso');
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projetos'] });
      queryClient.invalidateQueries({ queryKey: ['projeto-historico'] });
    }
  });
}

export function useAtualizarProjetoDados() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projetoId,
      dados,
    }: {
      projetoId: number;
      dados: {
        valor_projeto?: number;
        departamento?: string;
        observacoes?: string;
        empresa_responsavel_id?: number | null;
        empresa_responsavel?: string;
        cnpj_empresa?: string;
        telefone_empresa?: string;
        email_empresa?: string;
        is_projeto_elevo?: boolean;
        id_projeto_elevo?: string;
        empresa_projeto_externo_id?: string | null;
        consultor_nome?: string;
        consultor_celular?: string;
        consultor_cpf_cnpj?: string;
        consultor_email?: string;
        instalador_nome?: string;
        instalador_cpf_cnpj?: string;
        instalador_contato?: string;
        vendedor_nome?: string;
        vendedor_celular?: string;
        vendedor_cpf_cnpj?: string;
      };
    }) => {
      const updatePayload = {
        ...dados,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("projetos")
        .update(updatePayload)
        .eq("id", projetoId);

      if (error) {
        console.error("Erro ao atualizar projeto:", error);
        throw error;
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projetos"] });
      queryClient.invalidateQueries({ queryKey: ["projeto"] });
    },
  });
}

export function useProjetosAtivos() {
  return useQuery({
    queryKey: ['projetos-ativos'],
    queryFn: async () => {
      console.log('🔍 Iniciando busca de projetos ativos...');
      
      try {
        const { data: projetosData, error: projetosError } = await supabase
          .from('projetos')
          .select('*')
          .in('status', ['ativo', 'em_andamento', 'em andamento'])
          .order('created_at', { ascending: false });

        if (projetosError) {
          console.error('❌ Erro ao buscar projetos ativos:', projetosError);
          throw projetosError;
        }

        console.log('✅ Projetos ativos encontrados:', projetosData?.length || 0);
        return projetosData as Projeto[];
      } catch (error) {
        console.error('💥 Erro geral na busca de projetos ativos:', error);
        throw error;
      }
    },
    staleTime: 30000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });
}

export type PreviewExclusaoProjeto = {
  ok: boolean;
  projeto_id: number;
  cliente_id: number;
  cliente_nome?: string | null;
  projeto?: {
    valor_projeto?: number | null;
    valor_seguro?: number | null;
    orcamento_valor_total?: number | null;
    status?: string | null;
  };
  counts: {
    orcamentos: number;
    pedidos: number;
    contratos: number;
    parcelas: number;
    transacoes_financeiras: number;
    agenda: number;
    sinistros: number;
    sinistro_comentarios: number;
    projeto_equipamentos: number;
    orcamento_equipamentos: number;
    projeto_historico: number;
  };
  items?: {
    orcamentos: Array<{
      id: number;
      valor_total?: number | null;
      valor_seguro?: number | null;
      status?: string | null;
    }>;
    pedidos: Array<{
      id: number;
      numero_pedido?: string | null;
      valor_total?: number | null;
      status?: string | null;
    }>;
    contratos: Array<{
      id: number;
      numero_contrato?: string | null;
      valor?: number | null;
      status?: string | null;
    }>;
    parcelas: Array<{
      id: number;
      numero_parcela?: number | null;
      total_parcelas?: number | null;
      valor?: number | null;
      status?: string | null;
      data_vencimento?: string | null;
    }>;
    transacoes_financeiras: Array<{
      id: number;
      tipo?: string | null;
      valor?: number | null;
      status?: string | null;
      data_vencimento?: string | null;
    }>;
    agenda: Array<{
      id: number;
      titulo?: string | null;
      tipo?: string | null;
      data_inicio?: string | null;
      status?: string | null;
    }>;
    sinistros: Array<{
      id: number;
      numero_sinistro?: string | null;
      tipo?: string | null;
      valor_estimado?: number | null;
      status?: string | null;
    }>;
    projeto_equipamentos: Array<{
      id: number;
      quantidade?: number | null;
      preco_unitario?: number | null;
      valor?: number | null;
    }>;
    orcamento_equipamentos: Array<{
      id: number;
      orcamento_id?: number | null;
      quantidade?: number | null;
      preco_unitario?: number | null;
      valor?: number | null;
    }>;
  };
};

export type ExclusaoProjetoResult = {
  ok: boolean;
  projeto_id: number;
  cliente_id: number;
  deleted: Record<string, number>;
};

export function usePreviewExclusaoProjeto(projetoId: number | null) {
  return useQuery({
    queryKey: ['preview-exclusao-projeto', projetoId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('preview_exclusao_projeto', {
        p_projeto_id: projetoId,
      });
      if (error) throw error;
      return data as PreviewExclusaoProjeto;
    },
    enabled: projetoId != null,
    staleTime: 0,
  });
}

const PROJETO_DELETE_INVALIDATE = [
  'projetos',
  'orcamentos',
  'pedidos',
  'contratos',
  'transacoes_financeiras',
  'parcelas',
  'agenda',
  'sinistros',
] as const;

export function useExcluirProjeto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projetoId: number) => {
      const { data, error } = await supabase.rpc('excluir_projeto_cascata', {
        p_projeto_id: projetoId,
      });
      if (error) throw error;
      return data as ExclusaoProjetoResult;
    },
    onSuccess: () => {
      PROJETO_DELETE_INVALIDATE.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });
    },
  });
}
